import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { formatCurrency, formatRelativeTime, getStatusStyle } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Check, X, Clock, User, TrendingUp } from 'lucide-react';

const AdminPayoutsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchPayouts();
  }, [activeTab]);

  const fetchPayouts = async () => {
    setIsLoading(true);
    try {
      const params = activeTab !== 'all' ? { status: activeTab } : {};
      const res = await adminApi.getPayouts(params);
      setPayouts(res.data);
    } catch (error) {
      toast.error('Failed to load payouts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (payoutId) => {
    try {
      await adminApi.approvePayout(payoutId);
      toast.success('Payout approved');
      fetchPayouts();
    } catch (error) {
      toast.error('Failed to approve payout');
    }
  };

  const handleReject = async (payoutId) => {
    try {
      await adminApi.rejectPayout(payoutId, 'Rejected by admin');
      toast.success('Payout rejected');
      fetchPayouts();
    } catch (error) {
      toast.error('Failed to reject payout');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Payouts
            </h1>
            <p className="text-muted-foreground">Manage creator payouts</p>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="pending">
              Pending
              {payouts.filter(p => p.status === 'pending').length > 0 && (
                <span className="ml-2 w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center">
                  {payouts.filter(p => p.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="processing">Processing</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab}>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-24 w-full rounded-xl" />
                ))}
              </div>
            ) : payouts.length === 0 ? (
              <div className="bg-card border border-white/10 rounded-xl p-12 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold mb-2">No payouts</h3>
                <p className="text-muted-foreground">No payouts in this category</p>
              </div>
            ) : (
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Creator</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Campaign</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((payout) => {
                        const statusStyle = getStatusStyle(payout.status);
                        
                        return (
                          <tr 
                            key={payout.payout_id} 
                            className="border-b border-white/10 hover:bg-white/5"
                            data-testid={`payout-${payout.payout_id}`}
                          >
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                <span className="font-medium">{payout.user_name}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-muted-foreground" />
                                <span>{payout.campaign_name}</span>
                              </div>
                            </td>
                            <td className="p-4 font-medium">
                              {formatCurrency(payout.amount)}
                            </td>
                            <td className="p-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                                {statusStyle.label}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {formatRelativeTime(payout.created_at)}
                            </td>
                            <td className="p-4">
                              {payout.status === 'pending' && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => handleApprove(payout.payout_id)}
                                    data-testid={`approve-${payout.payout_id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleReject(payout.payout_id)}
                                    data-testid={`reject-${payout.payout_id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPayoutsPage;
